import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';

const VECTOR_STORE_LIMIT_BYTES = 100 * 1024 * 1024; // 100MB

interface KnowledgeBaseVectorStore {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  openAiVectorStoreId: string;
  status: 'CREATING' | 'READY' | 'ERROR' | string;
  fileCount: number;
  usageBytes?: number;
  lastSyncedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  migration?: {
    required: boolean;
    canMigrate: boolean;
    reason: 'LEGACY_VECTOR_STORE' | 'LEGACY_KEY_CANNOT_ACCESS_VECTOR_STORE' | 'TENANT_OPENAI_KEY_MISSING' | string | null;
  };
}

interface KnowledgeBaseDocument {
  id: string;
  tenantId: string;
  vectorStoreId: string;
  originalName: string;
  mimeType?: string | null;
  size?: number | null;
  status: 'UPLOADING' | 'PROCESSING' | 'READY' | 'FAILED' | 'REMOVED' | string;
  errorMessage?: string | null;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

interface ApiListDocumentsResponse {
  success?: boolean;
  documents?: KnowledgeBaseDocument[];
}

interface ApiVectorStoreResponse {
  success?: boolean;
  vectorStore?: KnowledgeBaseVectorStore;
  message?: string;
  totals?: {
    filesFound: number;
    migrated: number;
    failed: number;
  };
}

interface ApiUploadResponse {
  success?: boolean;
  document?: KnowledgeBaseDocument;
  error?: string;
  message?: string;
}

interface MediaItem {
  id: string;
  name?: string;
  originalName: string;
  description?: string | null;
  mimeType?: string | null;
  size: number;
  url: string;
  createdAt: string;
}

interface ApiMediaListResponse {
  success?: boolean;
  items?: MediaItem[];
  error?: string;
  message?: string;
}

interface ApiMediaUploadResponse {
  success?: boolean;
  item?: MediaItem;
  error?: string;
  message?: string;
}

export function KnowledgeBasePage({ embedded }: { embedded?: boolean } = {}) {
  const [activeTab, setActiveTab] = useState<'text' | 'media'>('text');
  const [vectorStore, setVectorStore] = useState<KnowledgeBaseVectorStore | null>(null);
  const [documents, setDocuments] = useState<KnowledgeBaseDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [deletingMediaId, setDeletingMediaId] = useState<string | null>(null);
  const [editingMedia, setEditingMedia] = useState<MediaItem | null>(null);
  const [editingMediaName, setEditingMediaName] = useState('');
  const [editingMediaDescription, setEditingMediaDescription] = useState('');
  const [savingMediaEdit, setSavingMediaEdit] = useState(false);
  const [formattingDocId, setFormattingDocId] = useState<string | null>(null);

  const [viewingDoc, setViewingDoc] = useState<{
    doc: KnowledgeBaseDocument;
    textContent: string | null;
    blobUrl: string | null;
    mimeType: string | null;
    loading: boolean;
  } | null>(null);
  const [replacingDoc, setReplacingDoc] = useState<KnowledgeBaseDocument | null>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      ...options.headers,
    };

    if (!(options.body instanceof FormData)) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const loadVectorStore = async () => {
    try {
      const response = await authenticatedFetch('/api/knowledge-base/vector-store');
      const data: ApiVectorStoreResponse = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = (data as any)?.error || (data as any)?.message || `HTTP ${response.status}`;
        setLastError(message);
        return;
      }

      if (data.vectorStore) {
        setVectorStore(data.vectorStore);
        setLastError(null);
      }
    } catch (error) {
      console.error('Erro ao carregar vector store:', error);
      setLastError('Erro ao carregar informações da base de conhecimento.');
    }
  };

  const openEditMedia = (item: MediaItem) => {
    setEditingMedia(item);
    setEditingMediaName((item.name || item.originalName || '').trim());
    setEditingMediaDescription((item.description || '').toString());
  };

  const closeEditMedia = () => {
    setEditingMedia(null);
    setEditingMediaName('');
    setEditingMediaDescription('');
    setSavingMediaEdit(false);
  };

  const handleSaveMediaEdit = async () => {
    if (!editingMedia) return;

    const trimmedName = editingMediaName.trim();
    if (!trimmedName) {
      toast.error('O nome da mídia é obrigatório.');
      return;
    }

    const trimmedDescription = editingMediaDescription.trim();
    const payload = {
      name: trimmedName,
      description: trimmedDescription ? trimmedDescription : null
    };

    try {
      setSavingMediaEdit(true);
      const response = await authenticatedFetch(`/api/media-library/${editingMedia.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = (data as any).error || (data as any).message || `Erro ao atualizar mídia (HTTP ${response.status})`;
        toast.error(message);
        return;
      }

      toast.success('Mídia atualizada com sucesso.');
      closeEditMedia();
      await loadMedia();
    } catch (error) {
      console.error('Erro ao atualizar mídia:', error);
      toast.error('Erro ao atualizar mídia.');
    } finally {
      setSavingMediaEdit(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await authenticatedFetch('/api/knowledge-base/files');
      const data: ApiListDocumentsResponse = await response.json().catch(() => ({} as ApiListDocumentsResponse));

      if (!response.ok) {
        const message = (data as any)?.error || (data as any)?.message || `HTTP ${response.status}`;
        setLastError(message);
        return;
      }

      setDocuments(data.documents || []);
      setLastError(null);
    } catch (error) {
      console.error('Erro ao carregar documentos da base de conhecimento:', error);
      setLastError('Erro ao carregar documentos da base de conhecimento.');
    }
  };

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadVectorStore(), loadDocuments(), loadMedia()]);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const loadMedia = async () => {
    try {
      setLoadingMedia(true);
      const response = await authenticatedFetch('/api/media-library');
      const data: ApiMediaListResponse = await response.json().catch(() => ({} as ApiMediaListResponse));

      if (!response.ok) {
        const message = data.error || data.message || `HTTP ${response.status}`;
        toast.error(message);
        return;
      }

      setMediaItems(data.items || []);
    } catch (error) {
      console.error('Erro ao carregar mídias:', error);
      toast.error('Erro ao carregar mídias.');
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast.error('Tamanho máximo por arquivo é 25MB.');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};
      if (token) {
        (headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/knowledge-base/files', {
        method: 'POST',
        body: formData,
        headers,
      });

      const data: ApiUploadResponse = await response.json().catch(() => ({} as ApiUploadResponse));

      if (!response.ok || data.error) {
        const message = data.error || data.message || `Erro ao enviar arquivo (HTTP ${response.status})`;
        toast.error(message);
        setLastError(message);
        return;
      }

      toast.success('Arquivo adicionado à base de conhecimento.');
      await loadAll();
      if (data.document) {
        await handleViewContent(data.document);
      }
    } catch (error) {
      console.error('Erro ao enviar arquivo para base de conhecimento:', error);
      toast.error('Erro ao enviar arquivo.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleMediaFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingMedia(true);

      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};
      if (token) {
        (headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/media-library', {
        method: 'POST',
        body: formData,
        headers,
      });

      const data: ApiMediaUploadResponse = await response.json().catch(() => ({} as ApiMediaUploadResponse));

      if (!response.ok || data.error) {
        const message = data.error || data.message || `Erro ao enviar mídia (HTTP ${response.status})`;
        toast.error(message);
        return;
      }

      toast.success('Mídia enviada com sucesso.');
      await loadMedia();
    } catch (error) {
      console.error('Erro ao enviar mídia para biblioteca:', error);
      toast.error('Erro ao enviar mídia.');
    } finally {
      setUploadingMedia(false);
      event.target.value = '';
    }
  };

  const handleViewContent = async (doc: KnowledgeBaseDocument) => {
    setViewingDoc({ doc, textContent: null, blobUrl: null, mimeType: null, loading: true });
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/knowledge-base/files/${doc.id}/content`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        toast.error((data as any).error || 'Erro ao carregar conteúdo.');
        setViewingDoc(null);
        return;
      }
      const contentType = response.headers.get('content-type') || doc.mimeType || '';
      const isText = /text\/|json|csv|markdown/.test(contentType);
      if (isText) {
        const text = await response.text();
        const blob = new Blob([text], { type: contentType });
        const url = URL.createObjectURL(blob);
        setViewingDoc({ doc, textContent: text, blobUrl: url, mimeType: contentType, loading: false });
      } else {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setViewingDoc({ doc, textContent: null, blobUrl: url, mimeType: contentType, loading: false });
      }
    } catch {
      toast.error('Erro ao carregar conteúdo do arquivo.');
      setViewingDoc(null);
    }
  };

  const closeViewingDoc = () => {
    if (viewingDoc?.blobUrl) URL.revokeObjectURL(viewingDoc.blobUrl);
    setViewingDoc(null);
  };

  const handleFormatWithAi = async (doc: KnowledgeBaseDocument) => {
    const ok = confirm('Formatar este arquivo com IA e reindexar a base vetorial? Isso substitui os chunks atuais por chunks estruturados para melhorar a busca.');
    if (!ok) return;

    try {
      setFormattingDocId(doc.id);
      const response = await authenticatedFetch(`/api/knowledge-base/files/${doc.id}/format-ai`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.error) {
        const message = data.error || data.message || `Erro ao formatar arquivo (HTTP ${response.status})`;
        toast.error(message);
        return;
      }

      toast.success('Arquivo formatado e reindexado com IA.');
      if (viewingDoc?.doc.id === doc.id) {
        if (viewingDoc.blobUrl) URL.revokeObjectURL(viewingDoc.blobUrl);
        setViewingDoc({
          doc: data.document || doc,
          textContent: data.formattedText || viewingDoc.textContent,
          blobUrl: null,
          mimeType: 'text/plain; charset=utf-8',
          loading: false,
        });
      }
      await loadAll();
    } catch (error) {
      console.error('Erro ao formatar arquivo com IA:', error);
      toast.error('Erro ao formatar arquivo com IA.');
    } finally {
      setFormattingDocId(null);
    }
  };

  const handleStartReplace = (doc: KnowledgeBaseDocument) => {
    setReplacingDoc(doc);
    setTimeout(() => replaceInputRef.current?.click(), 50);
  };

  const handleReplaceFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!replacingDoc) return;
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) { setReplacingDoc(null); return; }

    if (file.size > 25 * 1024 * 1024) {
      toast.error('Tamanho máximo por arquivo é 25MB.');
      setReplacingDoc(null);
      return;
    }

    const docBeingReplaced = replacingDoc;
    setReplacingDoc(null);

    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};
    if (token) (headers as Record<string, string>).Authorization = `Bearer ${token}`;

    const loadingToast = toast.loading(`Substituindo "${docBeingReplaced.originalName}"...`);
    try {
      const response = await fetch(`/api/knowledge-base/files/${docBeingReplaced.id}`, {
        method: 'PUT',
        body: formData,
        headers,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || (data as any).error) {
        toast.error((data as any).error || 'Erro ao substituir arquivo.', { id: loadingToast });
        return;
      }
      toast.success('Arquivo substituído com sucesso.', { id: loadingToast });
      await loadAll();
    } catch {
      toast.error('Erro ao substituir arquivo.', { id: loadingToast });
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!confirm('Tem certeza que deseja remover este documento da base de conhecimento?')) {
      return;
    }

    try {
      setDeletingId(documentId);
      const response = await authenticatedFetch(`/api/knowledge-base/files/${documentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = data.error || data.message || `Erro ao remover documento (HTTP ${response.status})`;
        toast.error(message);
        return;
      }

      toast.success('Documento removido com sucesso.');
      await loadAll();
    } catch (error) {
      console.error('Erro ao remover documento:', error);
      toast.error('Erro ao remover documento.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteMedia = async (mediaId: string) => {
    if (!confirm('Tem certeza que deseja remover esta mídia?')) {
      return;
    }

    try {
      setDeletingMediaId(mediaId);
      const response = await authenticatedFetch(`/api/media-library/${mediaId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = (data as any).error || (data as any).message || `Erro ao remover mídia (HTTP ${response.status})`;
        toast.error(message);
        return;
      }

      toast.success('Mídia removida com sucesso.');
      await loadMedia();
    } catch (error) {
      console.error('Erro ao remover mídia:', error);
      toast.error('Erro ao remover mídia.');
    } finally {
      setDeletingMediaId(null);
    }
  };

  const handleSyncVectorStore = async () => {
    try {
      setSyncing(true);
      const response = await authenticatedFetch('/api/knowledge-base/vector-store/sync', {
        method: 'POST',
      });

      const data: ApiVectorStoreResponse = await response.json().catch(() => ({} as ApiVectorStoreResponse));

      if (!response.ok) {
        const message = (data as any)?.error || (data as any)?.message || `Erro ao sincronizar (HTTP ${response.status})`;
        toast.error(message);
        return;
      }

      if (data.vectorStore) {
        setVectorStore(data.vectorStore);
      }

      toast.success('Base de conhecimento sincronizada com sucesso.');
      await loadDocuments();
    } catch (error) {
      console.error('Erro ao sincronizar vector store:', error);
      toast.error('Erro ao sincronizar base de conhecimento.');
    } finally {
      setSyncing(false);
    }
  };

  const handleMigrateVectorStore = async () => {
    if (!confirm('Reindexar seus documentos na nova base vetorial da IntegraIA? Esse processo pode demorar alguns minutos.')) {
      return;
    }

    try {
      setMigrating(true);
      const response = await authenticatedFetch('/api/knowledge-base/vector-store/migrate', {
        method: 'POST',
      });

      const data: ApiVectorStoreResponse = await response.json().catch(() => ({} as ApiVectorStoreResponse));

      if (!response.ok) {
        const message = (data as any)?.error || (data as any)?.message || `Erro ao migrar (HTTP ${response.status})`;
        toast.error(message);
        return;
      }

      if (data.vectorStore) {
        setVectorStore(data.vectorStore);
      }

      toast.success(data.message || 'Base migrada com sucesso.');
      await loadDocuments();
    } catch (error) {
      console.error('Erro ao migrar vector store:', error);
      toast.error('Erro ao migrar base de conhecimento.');
    } finally {
      setMigrating(false);
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString('pt-BR');
    } catch {
      return value;
    }
  };

  const formatFileSize = (bytes?: number | null) => {
    if (!bytes || bytes <= 0) return '-';
    const units = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
  };

  const isImageMedia = (mimeType?: string | null) => {
    return !!mimeType && mimeType.startsWith('image/');
  };

  const isVideoMedia = (mimeType?: string | null) => {
    return !!mimeType && mimeType.startsWith('video/');
  };

  const isAudioMedia = (mimeType?: string | null) => {
    return !!mimeType && mimeType.startsWith('audio/');
  };

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'READY':
        return 'bg-green-100 text-green-800';
      case 'PROCESSING':
      case 'UPLOADING':
        return 'bg-blue-100 text-blue-800';
      case 'FAILED':
      case 'REMOVED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'READY': return 'Pronto';
      case 'PROCESSING': return 'Processando';
      case 'UPLOADING': return 'Enviando';
      case 'FAILED': return 'Falhou';
      case 'REMOVED': return 'Removido';
      default: return status;
    }
  };

  const getVectorStoreStatusLabel = (status: string | undefined) => {
    switch (status) {
      case 'READY': return 'Pronto';
      case 'CREATING': return 'Criando';
      case 'ERROR': return 'Erro';
      default: return status || '-';
    }
  };

  const usageBytes = vectorStore?.usageBytes ?? 0;
  const usageMB = (usageBytes / 1024 / 1024).toFixed(2);
  const usagePercent = Math.min((usageBytes / VECTOR_STORE_LIMIT_BYTES) * 100, 100);
  const usageBarColor = usagePercent > 85 ? 'bg-red-500' : usagePercent > 60 ? 'bg-yellow-500' : 'bg-blue-500';

  return (
    <>
      {/* Input oculto para substituição de arquivo */}
      <input
        ref={replaceInputRef}
        type="file"
        className="hidden"
        onChange={handleReplaceFileChange}
        accept=".pdf,.txt,.md,.csv,.json,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
      />

      {!embedded && (
        <Header
          title="Base de Reconhecimento"
          subtitle="Gerencie os documentos e mídias utilizados pela IA."
        />
      )}

      <div className="flex flex-col">
        {/* Tabs */}
        <div className="border-b border-gray-200 px-6 bg-white">
          <nav className="flex gap-0" aria-label="Abas da base de reconhecimento">
            <button
              type="button"
              onClick={() => setActiveTab('text')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors focus:outline-none ${
                activeTab === 'text'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Base de Texto para IA
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('media')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors focus:outline-none ${
                activeTab === 'media'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Base de Mídias
              {mediaItems.length > 0 && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {mediaItems.length}
                </span>
              )}
            </button>
          </nav>
        </div>

        <div className="p-6 space-y-6">
          {lastError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <p className="font-semibold mb-1">Atenção</p>
              <p>{lastError}</p>
              <p className="mt-2 text-xs text-red-700">
                Se o erro persistir, verifique a conectividade com a base vetorial em "Configurações &gt; Integrações de IA".
              </p>
            </div>
          )}

          {/* ── ABA: Base de Texto para IA ── */}
          {activeTab === 'text' && (
            <div className="space-y-6">
              {/* Resumo + uso */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900">Resumo da Base Vetorial</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Cada tenant possui uma base vetorial dedicada. Os arquivos enviados são indexados para busca semântica pela IA.
                    </p>
                    {vectorStore && (
                      <p className="text-sm text-gray-700 mt-2">
                        <span className="font-medium">Nome da base:</span>{' '}
                        <span className="font-mono text-gray-900">{vectorStore.name}</span>
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-3 lg:min-w-[280px]">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500">Status:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(vectorStore?.status || 'CREATING')}`}>
                        {getVectorStoreStatusLabel(vectorStore?.status)}
                      </span>
                    </div>

                    {/* Barra de uso */}
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Uso da base vetorial</span>
                        <span className={`font-medium ${usagePercent > 85 ? 'text-red-600' : 'text-gray-700'}`}>
                          {usageMB} MB / 100 MB
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-2 rounded-full transition-all ${usageBarColor}`}
                          style={{ width: `${usagePercent}%` }}
                        />
                      </div>
                      {usagePercent > 85 && (
                        <p className="mt-1 text-xs text-red-600">Quase no limite! Remova documentos para liberar espaço.</p>
                      )}
                    </div>

                    <div className="text-xs text-gray-500 space-y-0.5">
                      <div>Arquivos indexados: <span className="font-medium">{vectorStore?.fileCount ?? 0}</span></div>
                      <div>Última sincronização: {formatDateTime(vectorStore?.lastSyncedAt)}</div>
                    </div>

                    <button
                      type="button"
                      onClick={handleSyncVectorStore}
                      disabled={syncing || loading || migrating}
                      className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {syncing ? 'Sincronizando...' : 'Sincronizar estado da base'}
                    </button>
                  </div>
                </div>

                {vectorStore?.migration?.required && (
                  <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-amber-900">Seus documentos precisam ser reindexados</p>
                        <p className="mt-1 text-sm text-amber-800">
                          A Base de Conhecimento foi atualizada. Reindexe os documentos para continuar usando a busca semântica. Arquivos sem cópia precisarão ser reenviados.
                        </p>
                        {!vectorStore.migration.canMigrate && (
                          <p className="mt-2 text-xs text-amber-900">
                            Reindexação automática indisponível. Reenvie os documentos manualmente.
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleMigrateVectorStore}
                        disabled={migrating || loading || !vectorStore.migration.canMigrate}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {migrating ? 'Reindexando...' : 'Reindexar documentos'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Upload */}
              <div className="bg-white rounded-xl shadow-sm border border-dashed border-blue-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Adicionar documentos</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Envie arquivos de texto, PDF, DOCX, planilhas e apresentações. Limite de 25MB por arquivo e 100MB total na base vetorial.
                </p>

                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  <label className={`flex-1 border-2 border-dashed rounded-lg px-4 py-6 text-center transition-colors ${
                    usagePercent >= 100
                      ? 'border-red-300 bg-red-50 cursor-not-allowed opacity-60'
                      : 'border-blue-200 cursor-pointer hover:border-blue-400 hover:bg-blue-50'
                  }`}>
                    <div className="text-3xl mb-2">📄</div>
                    <p className="text-sm font-medium text-blue-900">
                      {usagePercent >= 100
                        ? 'Limite de 100MB atingido'
                        : 'Clique aqui para selecionar um arquivo'}
                    </p>
                    <p className="text-xs text-blue-700 mt-1">Máx. 25MB por arquivo. Formatos: PDF, TXT, DOCX, CSV, XLSX, PPTX.</p>
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                      disabled={uploading || loading || usagePercent >= 100}
                    />
                  </label>

                  <div className="text-xs text-gray-500 max-w-xs">
                    <p className="mb-1 font-semibold text-gray-700">Boas práticas:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Prefira documentos com textos bem estruturados.</li>
                      <li>Evite scans de baixa qualidade ou imagens sem OCR.</li>
                      <li>Atualize documentos relevantes em vez de subir múltiplas versões.</li>
                    </ul>
                  </div>
                </div>

                {uploading && (
                  <p className="mt-3 text-xs text-blue-700">Enviando arquivo, aguarde...</p>
                )}
              </div>

              {/* Lista de documentos */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Documentos enviados</h2>
                    <p className="text-xs text-gray-500">Apenas documentos com status "Pronto" são usados na busca da IA.</p>
                  </div>
                  <div className="text-xs text-gray-500">
                    Total: <span className="font-medium">{documents.length}</span>
                  </div>
                </div>

                {loading ? (
                  <div className="p-6 text-center text-gray-500 text-sm">Carregando documentos...</div>
                ) : documents.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">
                    Nenhum documento enviado ainda. Adicione arquivos para começar a treinar sua base de conhecimento.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Arquivo</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Tamanho</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">Tipo</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Enviado em</th>
                          <th className="px-6 py-3 w-[360px]" />
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {documents.map((doc: KnowledgeBaseDocument) => (
                          <tr key={doc.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3">
                              <div className="flex flex-col gap-0.5">
                                <span className="font-medium text-gray-900 break-all" title={doc.originalName}>
                                  {doc.originalName}
                                </span>
                                {doc.errorMessage && (
                                  <span className="text-[11px] text-red-600 break-words">
                                    {doc.errorMessage}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-gray-700 text-xs">
                              {formatFileSize(doc.size ?? null)}
                            </td>
                            <td className="px-6 py-3 text-gray-500 text-xs break-all">
                              {doc.mimeType || '-'}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(doc.status)}`}>
                                {getStatusLabel(doc.status)}
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-gray-500 text-xs">
                              {formatDateTime(doc.createdAt)}
                            </td>
                            <td className="px-6 py-3 text-right text-xs">
                              <div className="flex flex-wrap items-center justify-end gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleViewContent(doc)}
                                  className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs"
                                  title="Visualizar conteúdo"
                                >
                                  Ver
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleFormatWithAi(doc)}
                                  disabled={formattingDocId === doc.id || doc.status === 'PROCESSING' || doc.status === 'UPLOADING'}
                                  className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50 text-xs"
                                  title="Organizar o arquivo com IA e reindexar a base vetorial"
                                >
                                  {formattingDocId === doc.id ? 'Formatando...' : 'Formatar IA'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleStartReplace(doc)}
                                  disabled={replacingDoc?.id === doc.id}
                                  className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50 text-xs"
                                  title="Substituir por nova versão"
                                >
                                  Substituir
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  disabled={deletingId === doc.id}
                                  className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 text-xs"
                                >
                                  {deletingId === doc.id ? 'Removendo...' : 'Remover'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── ABA: Base de Mídias ── */}
          {activeTab === 'media' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Biblioteca de Mídias</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Arquivos armazenados independentemente da base vetorial.
                    </p>
                    <p className="text-xs text-gray-500">
                      A descrição de cada mídia ajudará a IA a selecionar o momento certo de envio.
                    </p>
                  </div>
                  <label className="inline-flex items-center gap-2 px-4 py-2 border border-blue-200 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 cursor-pointer whitespace-nowrap self-start sm:self-auto">
                    <span>+ Enviar mídia</span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleMediaFileChange}
                      disabled={uploadingMedia}
                    />
                  </label>
                </div>

                {uploadingMedia && (
                  <div className="px-6 py-2 text-xs text-blue-700 bg-blue-50">Enviando mídia, aguarde...</div>
                )}

                {loadingMedia ? (
                  <div className="p-6 text-center text-gray-500 text-sm">Carregando mídias...</div>
                ) : mediaItems.length === 0 ? (
                  <div className="p-10 text-center text-gray-500 text-sm">
                    <div className="text-4xl mb-3">🖼️</div>
                    <p className="font-medium">Nenhuma mídia cadastrada ainda.</p>
                    <p className="text-xs mt-1">Envie imagens, vídeos, áudios, PDFs e outros arquivos para criar sua biblioteca.</p>
                  </div>
                ) : (
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {mediaItems.map((item: MediaItem) => (
                      <div
                        key={item.id}
                        className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm flex flex-col"
                      >
                        <div className="w-full h-44 bg-gray-50 flex items-center justify-center overflow-hidden">
                          {isImageMedia(item.mimeType) && item.url && (
                            <img
                              src={item.url}
                              alt={item.name || item.originalName}
                              className="w-full h-full object-cover"
                            />
                          )}
                          {isVideoMedia(item.mimeType) && item.url && (
                            <video
                              src={item.url}
                              className="w-full h-full object-cover"
                              controls
                            />
                          )}
                          {!isImageMedia(item.mimeType) && !isVideoMedia(item.mimeType) && isAudioMedia(item.mimeType) && item.url && (
                            <div className="w-full px-4">
                              <div className="text-3xl text-center mb-2">🎵</div>
                              <audio src={item.url} controls className="w-full" />
                            </div>
                          )}
                          {!isImageMedia(item.mimeType) &&
                            !isVideoMedia(item.mimeType) &&
                            !isAudioMedia(item.mimeType) && (
                              <div className="flex flex-col items-center gap-2 px-4">
                                <span className="text-4xl">📄</span>
                                <span className="text-xs text-gray-500 text-center break-all">
                                  {item.mimeType || 'Arquivo'}
                                </span>
                              </div>
                            )}
                        </div>
                        <div className="p-3 flex-1 flex flex-col gap-1">
                          <span className="font-medium text-gray-900 text-sm break-words" title={item.originalName}>
                            {item.name || item.originalName}
                          </span>
                          {item.description && item.description.trim().length > 0 && (
                            <span className="text-[11px] text-gray-600 line-clamp-3" title={item.description}>
                              {item.description}
                            </span>
                          )}
                          <div className="text-[11px] text-gray-500 flex flex-wrap gap-1 mt-0.5">
                            <span>{formatFileSize(item.size)}</span>
                            <span>•</span>
                            <span className="break-all">{item.mimeType || '-'}</span>
                            <span>•</span>
                            <span>{formatDateTime(item.createdAt)}</span>
                          </div>
                          <div className="mt-2 flex flex-col gap-2">
                            {item.url && (
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline truncate"
                              >
                                Abrir no navegador
                              </a>
                            )}
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEditMedia(item)}
                                className="flex-1 inline-flex items-center justify-center px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteMedia(item.id)}
                                disabled={deletingMediaId === item.id}
                                className="flex-1 inline-flex items-center justify-center px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 text-xs"
                              >
                                {deletingMediaId === item.id ? 'Removendo...' : 'Remover'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal visualizar conteúdo do documento */}
      {viewingDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-[1400px] h-[88vh] flex flex-col rounded-xl bg-white shadow-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">Conteúdo do arquivo</h3>
                <p className="text-xs text-gray-500 break-all mt-0.5">{viewingDoc.doc.originalName}</p>
              </div>
              <div className="flex items-center gap-2 ml-4 shrink-0">
                {viewingDoc.blobUrl && (
                  <a
                    href={viewingDoc.blobUrl}
                    download={viewingDoc.doc.originalName}
                    className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs"
                  >
                    Baixar
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => handleFormatWithAi(viewingDoc.doc)}
                  disabled={formattingDocId === viewingDoc.doc.id || viewingDoc.doc.status === 'PROCESSING' || viewingDoc.doc.status === 'UPLOADING'}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50 text-xs"
                >
                  {formattingDocId === viewingDoc.doc.id ? 'Formatando...' : 'Formatar arquivo com IA'}
                </button>
                <button
                  type="button"
                  onClick={closeViewingDoc}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Fechar
                </button>
              </div>
            </div>

            {!viewingDoc.doc.metadata?.aiFormatted && (
              <div className="border-b border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold">Recomendado antes de usar na base vetorial</p>
                    <p className="text-xs text-amber-800">
                      Formatar com IA organiza o arquivo em perguntas, respostas, tópicos e palavras-chave. Sem isso, a busca pode recuperar trechos misturados e responder pior.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFormatWithAi(viewingDoc.doc)}
                    disabled={formattingDocId === viewingDoc.doc.id || viewingDoc.doc.status === 'PROCESSING' || viewingDoc.doc.status === 'UPLOADING'}
                    className="inline-flex shrink-0 items-center justify-center rounded-md border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                  >
                    {formattingDocId === viewingDoc.doc.id ? 'Formatando...' : 'Formatar agora com IA'}
                  </button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-hidden">
              {viewingDoc.loading && (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">
                  Carregando conteúdo...
                </div>
              )}

              {!viewingDoc.loading && viewingDoc.textContent !== null && (
                <textarea
                  readOnly
                  value={viewingDoc.textContent}
                  className="w-full h-full p-4 font-mono text-xs text-gray-800 bg-gray-50 resize-none focus:outline-none border-0"
                  spellCheck={false}
                />
              )}

              {!viewingDoc.loading && viewingDoc.blobUrl && viewingDoc.mimeType?.includes('pdf') && (
                <iframe
                  src={viewingDoc.blobUrl}
                  className="w-full h-full border-0"
                  title={viewingDoc.doc.originalName}
                />
              )}

              {!viewingDoc.loading && viewingDoc.blobUrl && !viewingDoc.mimeType?.includes('pdf') && (
                <div className="h-full flex flex-col items-center justify-center gap-4 text-gray-500">
                  <span className="text-5xl">📄</span>
                  <p className="text-sm font-medium">{viewingDoc.doc.originalName}</p>
                  <p className="text-xs text-gray-400">{viewingDoc.mimeType}</p>
                  <p className="text-xs text-gray-400">Pré-visualização não disponível para este formato.</p>
                  <a
                    href={viewingDoc.blobUrl}
                    download={viewingDoc.doc.originalName}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                  >
                    Baixar arquivo
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal editar mídia */}
      {editingMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-lg border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Editar mídia</h3>
                <p className="text-xs text-gray-500 break-all">Arquivo: {editingMedia.originalName}</p>
              </div>
              <button
                type="button"
                onClick={closeEditMedia}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Fechar
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={editingMediaName}
                  onChange={(e) => setEditingMediaName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Ex: Cardápio, Tabela de preços, Apresentação institucional"
                  disabled={savingMediaEdit}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={editingMediaDescription}
                  onChange={(e) => setEditingMediaDescription(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 min-h-[110px]"
                  placeholder="Descreva quando essa mídia deve ser enviada e qual o contexto ideal..."
                  disabled={savingMediaEdit}
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  A descrição ajudará a IA a selecionar as mídias para enviar em momentos certos.
                </p>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeEditMedia}
                className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                disabled={savingMediaEdit}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveMediaEdit}
                className="inline-flex items-center px-3 py-2 rounded-md border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 text-sm disabled:opacity-50"
                disabled={savingMediaEdit}
              >
                {savingMediaEdit ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
